import { Platform } from '@angular/cdk/platform';
import { Injectable, inject, signal } from '@angular/core';
import { ForecastSeriesCompletedWithoutMeasure } from '@api-response-types/forecast-series.response';
import { ContentMode } from '@api-types/enums/export-content-mode.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { ExportForecastOptions } from '@api-types/types/export-forecast-options.interface';
import { FilterValue } from '@api-types/types/filter-value.type';
import { getForecastExportFileName } from '@libs/shared/helpers/forecast-export-file-name';
import { getPeriodsSapFormatFilename } from '@libs/shared/helpers/periods-sap-format-file-name';
import { finalize, tap } from 'rxjs';
import { ForecastVersionHttpClient } from '../http/forecast-version-http-client.service';

export type ExportForecastProps = {
  forecastVersionId: string;
  pipelineForecastId: string;
  appliedFilters: Map<string, FilterValue[]>;
  resolution: Resolution | undefined;
  minDate: string;
  maxDate: string;
  factorColumn: string | null;
  xlsxMetadataTranslationObject: Record<string, string>;
  exportType: 'CSV' | 'XLSX' | 'TXT' | 'ZIP';
  contentMode: ContentMode;
  selectedGroups: string[];
  selectedAdditionalData: string[];
  wideFormat: boolean;
  exportTarget: boolean;
  plnmgSapFormat: boolean;
  periodsSapFormat: boolean;
  detailMode: 'AGGREGATED' | 'DETAILED';
};
@Injectable({
  providedIn: 'root',
})
export class ExportForecastService {
  private readonly httpClient = inject(ForecastVersionHttpClient);
  private readonly platformService = inject(Platform);

  private readonly _isLoading = signal(false);
  public readonly isLoading = this._isLoading.asReadonly();

  exportForecast(
    forecastVersionId: string,
    exportOptions: ExportForecastOptions,
  ) {
    return this.httpClient
      .exportResultSet(forecastVersionId, exportOptions)
      .pipe(
        tap({ subscribe: () => this._isLoading.set(true) }),
        finalize(() => this._isLoading.set(false)),
      );
  }

  public async downloadFile(
    data: Blob,
    exportType: string,
    configName: string,
    forecastVersionCreatedAt: string,
    periodsSapFormat = false,
    forecastSeries?: ForecastSeriesCompletedWithoutMeasure,
  ) {
    if (!this.isCurrentBrowserSafariOrFirefox()) {
      // Chrome, Edge and Opera are currently supported by the File System Access API
      try {
        await this.downloadFileWithFileSavePicker(
          data,
          exportType,
          configName,
          forecastVersionCreatedAt,
          periodsSapFormat,
          forecastSeries,
        );
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled the file picker
          return;
        }

        console.error('File System Access API not supported');
        console.error(error);
        this.downloadFileDirectly(
          data,
          exportType,
          configName,
          forecastVersionCreatedAt,
          periodsSapFormat,
          forecastSeries,
        );
      }
    } else {
      // Safari and Firefox currently do not support the File System Access API
      this.downloadFileDirectly(
        data,
        exportType,
        configName,
        forecastVersionCreatedAt,
        periodsSapFormat,
        forecastSeries,
      );
    }
  }

  private isCurrentBrowserSafariOrFirefox(): boolean {
    const isSafari: boolean = this.platformService.SAFARI;
    const isFirefox: boolean = this.platformService.FIREFOX;

    return isSafari || isFirefox;
  }

  private async downloadFileWithFileSavePicker(
    data: Blob,
    exportType: string,
    configName: string,
    forecastVersionCreatedAt: string,
    periodsSapFormat = false,
    forecastSeries?: ForecastSeriesCompletedWithoutMeasure,
  ) {
    const fileHandle = await this.getFileHandle(
      configName,
      exportType,
      forecastVersionCreatedAt,
      periodsSapFormat,
      forecastSeries,
    );
    if (fileHandle) {
      await this.saveFileAs(data, fileHandle);
    }
  }

  private downloadFileDirectly(
    data: Blob,
    exportType: string,
    configName: string,
    forecastVersionCreatedAt: string,
    periodsSapFormat = false,
    forecastSeries?: ForecastSeriesCompletedWithoutMeasure,
  ) {
    let defaultFilename = getForecastExportFileName(
      configName,
      forecastVersionCreatedAt,
    );

    if (periodsSapFormat) {
      if (forecastSeries) {
        defaultFilename = getPeriodsSapFormatFilename(
          undefined,
          undefined,
          forecastSeries,
        );
      }
    }

    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    const url = window.URL.createObjectURL(data);
    a.href = url;
    a.download = `${defaultFilename}.${exportType.toLowerCase()}`;
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  private async getFileHandle(
    configName: string,
    exportType: string,
    forecastVersionCreatedAt: string,
    periodsSapFormat = false,
    forecastSeries?: ForecastSeriesCompletedWithoutMeasure,
  ) {
    let defaultFilename = getForecastExportFileName(
      configName,
      forecastVersionCreatedAt,
    );

    if (periodsSapFormat) {
      if (forecastSeries) {
        defaultFilename = getPeriodsSapFormatFilename(
          undefined,
          undefined,
          forecastSeries,
        );
      }
    }

    const excelType: FilePickerAcceptType[] = [
      {
        description: 'Excel Workbook',
        accept: {
          'application/vnd.ms-excel': ['.xlsx'],
        },
      },
    ];

    const csvType: FilePickerAcceptType[] = [
      {
        description: 'CSV',
        accept: {
          'text/csv': ['.csv'],
        },
      },
    ];
    const zipType: FilePickerAcceptType[] = [
      {
        description: 'ZIP',
        accept: {
          'application/zip': ['.zip'],
        },
      },
    ];

    let fileType;

    if (exportType === 'CSV') {
      fileType = csvType;
    } else if (exportType === 'XLSX') {
      fileType = excelType;
    } else {
      fileType = zipType;
    }

    return window.showSaveFilePicker({
      suggestedName: defaultFilename,
      types: fileType,
    });
  }

  private async saveFileAs(contents: Blob, fileHandle: FileSystemFileHandle) {
    try {
      const writable = await fileHandle.createWritable({
        keepExistingData: true,
      });
      await writable.write(contents);
      await writable.close();
    } catch (ex: unknown) {
      console.error(ex);
    }
  }
}
